import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FIRMWARE_HARDWARE, Firmware, FirmwareType } from '@ixicare/resources';
import { Alert, Button, Checkbox, FileDrop, Input, Modal, Select } from '@ixicare/ui';
import { t } from '~i18n';
import { useStores } from '~store/utils';
import { humaniseErrors } from '~utils/error';
import { FIRMWARES_QUERY_KEY } from '../hooks/useFirmwares';
import { firmwareTypeOptionLabel } from '../utils/firmwareLabels';
import styles from './FirmwareUploadModal.module.css';

// Firmware ships as .zip (BLE), .apk (Lagenio) or .bin (gateways). Browsers report a .bin as
// octet-stream, as macbinary (Chrome on macOS) or not at all, so FileDrop's image/pdf default would
// reject every real file.
const FIRMWARE_FILE_TYPES = [
  '',
  'application/octet-stream',
  'application/macbinary',
  'application/x-macbinary',
  'application/zip',
  'application/x-zip-compressed',
  'application/vnd.android.package-archive',
];
// Only what is still flashed today; the backend enum also lists retired modules and config types.
const UPLOADABLE_FIRMWARE_TYPES = Object.keys(FIRMWARE_HARDWARE) as FirmwareType[];
const MAX_FIRMWARE_SIZE = 50 * 1024 * 1024;
const VERSION_PATTERN = /^[0-9A-Za-z._-]+$/;

// "MINEW_GW_BLE_1.5.4.bin" or "2.2.7-B10.zip" -> the first dotted number in the name.
const versionFromFileName = (name: string) => /\d+(?:\.\d+)+/.exec(name)?.[0];

interface FirmwareUploadModalProps {
  onClose: () => void;
  defaultFirmwareType?: FirmwareType;
}

export const FirmwareUploadModal = ({ onClose, defaultFirmwareType }: FirmwareUploadModalProps) => {
  const { notifications } = useStores();
  const queryClient = useQueryClient();
  const [firmwareType, setFirmwareType] = React.useState<FirmwareType | undefined>(defaultFirmwareType);
  const [version, setVersion] = React.useState('');
  // A version typed by hand is never overwritten by one guessed from the file name.
  const [isVersionTyped, setIsVersionTyped] = React.useState(false);
  const [file, setFile] = React.useState<File>();
  const [isFileValid, setIsFileValid] = React.useState(true);
  // Latest marks a stable version, the one customers update to, so a new upload is not it by default.
  const [isLatest, setIsLatest] = React.useState(false);

  const versionError = version && !VERSION_PATTERN.test(version) ? t('firmware.upload.versionInvalid') : undefined;
  const canSubmit = !!firmwareType && !!version && !versionError && !!file && isFileValid;

  const uploadMutation = useMutation({
    mutationFn: () => Firmware.createFirmware({ firmwareType: firmwareType!, version, file: file!, isLatest }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FIRMWARES_QUERY_KEY });
      notifications.addUserActionNotification({
        type: 'success' as any,
        notificationType: 'success',
        message: t('firmware.upload.success'),
      });
      onClose();
    },
  });

  const onFileChange = React.useCallback(
    (next?: File) => {
      setIsFileValid(true);
      setFile(next);
      const proposed = next && versionFromFileName(next.name);
      if (proposed && !isVersionTyped) setVersion(proposed);
    },
    [isVersionTyped]
  );

  const onInvalidFile = ({ isSizeInvalid }: { isSizeInvalid?: boolean } = {}) => {
    notifications.addUserActionNotification({
      type: 'error' as any,
      notificationType: 'error',
      message: isSizeInvalid ? t('firmware.upload.fileTooLarge') : t('firmware.upload.fileTypeInvalid'),
    });
  };

  return (
    <Modal center>
      <h3 className="text-center mb-10">{t('firmware.upload.title')}</h3>
      {uploadMutation.error && <Alert type="error">{humaniseErrors(uploadMutation.error as Error).title}</Alert>}
      <div className={styles.Fields}>
        <Select
          as="input"
          required
          label={t('firmware.firmwareType')}
          placeholder={t('firmware.upload.selectType')}
          value={firmwareType}
          onChange={setFirmwareType}
          options={UPLOADABLE_FIRMWARE_TYPES.map((type) => ({ value: type, label: firmwareTypeOptionLabel(type) }))}
        />
        <Input
          required
          label={t('firmware.version')}
          placeholder="1.2.3"
          value={version}
          error={versionError}
          onValueChange={(value) => {
            setIsVersionTyped(value.trim() !== '');
            setVersion(value.trim());
          }}
        />
        <FileDrop
          label={t('firmware.upload.dropFile')}
          clearText={t('site.form.image.clearSelection')}
          value={file}
          onChange={onFileChange}
          onInvalid={onInvalidFile}
          isFileValid={isFileValid}
          setIsFileValid={setIsFileValid}
          maxSize={MAX_FIRMWARE_SIZE}
          allowedFileTypes={FIRMWARE_FILE_TYPES}
        />
        <Checkbox
          id="firmware-upload-latest"
          label={t('firmware.upload.markLatest')}
          checked={isLatest}
          onCheckedChange={setIsLatest}
        />
      </div>
      <Button
        className="mb-15"
        isBlock
        buttonType="success"
        type="button"
        disabled={!canSubmit || uploadMutation.isPending}
        loading={uploadMutation.isPending}
        onClick={() => uploadMutation.mutate()}
      >
        {t('site.floors.upload')}
      </Button>
      <Button isBlock buttonType="link" disabled={uploadMutation.isPending} onClick={onClose}>
        {t('global.cancel')}
      </Button>
    </Modal>
  );
};
